import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.jsx';
import { AuthProvider } from './auth/AuthContext.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import './styles/index.css';

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
});

// Register service worker (PWA) once in production, with an update strategy
// that catches new deploys without forcing the user to manually reload.
//
//  1. Periodically ask the SW to check for an updated /sw.js (every 60s).
//  2. When a new SW takes control, reload the page so the user picks up the
//     fresh HTML + asset bundle. We only auto-reload when the user is idle
//     (tab hidden OR no interaction for 2 minutes) so we never interrupt
//     someone mid-voice-note. Active users get the new version on their next
//     navigation, which is fast because nginx serves index.html with no-cache.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register('/sw.js').then((registration) => {
    setInterval(() => registration.update().catch(() => {}), 60_000);

    let lastInteraction = Date.now();
    const bumpInteraction = () => { lastInteraction = Date.now(); };
    ['pointerdown', 'keydown', 'touchstart'].forEach(ev =>
      document.addEventListener(ev, bumpInteraction, { passive: true })
    );

    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      const idleMs = Date.now() - lastInteraction;
      if (document.hidden || idleMs > 2 * 60_000) {
        reloading = true;
        window.location.reload();
      }
      // else: user is mid-task; new version applies on their next manual reload/navigate.
    });
  }).catch(() => {});
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={qc}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
